import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import Navbar from '../components/Navbar'
import PageHeader from '../components/PageHeader'
import { useAuth } from '../hooks/useAuth'
import { usePractice } from '../hooks/usePractice'
import { getClients, getRequests, deleteRequest, updateClientReminders, updateClientDetails, unarchiveClient } from '../lib/supabase'
import { useToast } from '../components/Toast'
import usePageTitle from '../hooks/usePageTitle'
import { formatDate } from '../lib/csvParser'

function getRequestCounts(req) {
  const txs = req.transactions || []
  const total   = txs.length
  const uploaded = txs.filter(t => t.status === 'uploaded').length
  const lost    = txs.filter(t => t.status === 'lost').length
  const pending = txs.filter(t => t.status === 'pending').length
  return { total, uploaded, lost, pending }
}

function isCompleted(req) {
  const { total, pending } = getRequestCounts(req)
  return total > 0 && pending === 0
}

function CopyLinkBtn({ token }) {
  const [copied, setCopied] = useState(false)
  const url = `${window.location.origin}/request/${token}`
  const copy = async () => {
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button className="btn btn-secondary btn-sm" onClick={copy} title={url}>
      {copied ? '✓ Copied!' : '🔗 Copy link'}
    </button>
  )
}

// Editable single-line field for the client details form. Label above, input
// below. Used inside the edit form.
function ClientEditField({ label, value, onChange, placeholder, type = 'text', maxLength }) {
  return (
    <div>
      <div style={{
        fontSize: '0.74rem', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        color: 'var(--ink-muted)', marginBottom: 4,
      }}>
        {label}
      </div>
      <input
        type={type}
        className="input"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    </div>
  )
}

export default function ClientDetail() {
  const { clientId } = useParams()
  const { user }     = useAuth()
  const { practiceId, role } = usePractice()
  const toast        = useToast()
  usePageTitle(client?.name || 'Client')

  const [client, setClient]     = useState(null)
  const [requests, setRequests] = useState([])
  const [loading, setLoading]   = useState(true)
  const [selected, setSelected] = useState(new Set())
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [activeTab, setActiveTab]       = useState('outstanding')

  // ─── Client details card edit state ─────────────────────────────────────
  // Editing client info is inline on the same card: clicking Edit reveals
  // form inputs in place of the read-only display. Drafts hold the working
  // copy until Save commits via updateClientDetails.
  const [editingDetails, setEditingDetails] = useState(false)
  const [detailsDrafts, setDetailsDrafts]   = useState({})
  const [savingDetails, setSavingDetails]   = useState(false)

  // When notes are long they collapse to a truncated preview with a "more"
  // toggle to expand inline. Short notes (under the threshold) skip the
  // collapse and just show fully. Threshold is generous so most notes won't
  // need the toggle at all.
  const NOTES_COLLAPSE_THRESHOLD = 120 // characters
  const [notesExpanded, setNotesExpanded] = useState(false)

  // Unarchive handler. Just clears the archived flag and reloads the
  // client. Open requests previously cancelled by an archive-with-cancel
  // are NOT auto-reopened, since reopening should be a per-request
  // decision (the bookkeeper might want only some back).
  const [unarchiving, setUnarchiving] = useState(false)
  const handleUnarchive = async () => {
    if (!client) return
    setUnarchiving(true)
    const { error } = await unarchiveClient(clientId)
    setUnarchiving(false)
    if (error) { toast(error.message, 'error'); return }
    toast(`${client.name} restored to active clients`, 'success')
    load()
  }

  const load = async () => {
    if (!practiceId) return
    const { data: clients } = await getClients(practiceId)
    const c = clients?.find(x => x.id === clientId)
    if (!c) { toast('Client not found', 'error'); return }
    setClient(c)

    const { data: reqs, error } = await getRequests(clientId)
    if (error) toast(error.message, 'error')
    else setRequests(reqs || [])
    setLoading(false)
    setSelected(new Set())
  }

  useEffect(() => { if (practiceId) load() }, [clientId, practiceId])

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = (visibleRequests) => {
    const visibleIds = visibleRequests.map(r => r.id)
    const allSelected = visibleIds.every(id => selected.has(id))
    setSelected(prev => {
      const next = new Set(prev)
      allSelected
        ? visibleIds.forEach(id => next.delete(id))
        : visibleIds.forEach(id => next.add(id))
      return next
    })
  }

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selected.size} request${selected.size !== 1 ? 's' : ''}? This will remove all their transactions and uploaded files.`)) return
    setBulkDeleting(true)
    for (const id of selected) await deleteRequest(id)
    setBulkDeleting(false)
    toast(`${selected.size} request${selected.size !== 1 ? 's' : ''} deleted`, 'default')
    load()
  }

  const handleDeleteSingle = async (req) => {
    if (!window.confirm(`Delete "${req.title}"? This will remove all transactions and uploaded files.`)) return
    const { error } = await deleteRequest(req.id)
    if (error) { toast(error.message, 'error'); return }
    toast('Request deleted', 'default')
    load()
  }

  // ─── Per-client reminder toggle ─────────────────────────────────────────────
  // Optimistic: flip the UI immediately, then write to the DB. Reverts the
  // toggle if the save fails.
  const handleToggleClientReminders = async () => {
    if (!client) return
    const current = client.reminders_enabled !== false
    const next    = !current
    setClient({ ...client, reminders_enabled: next })
    const { error } = await updateClientReminders(clientId, next)
    if (error) {
      toast(error.message, 'error')
      setClient({ ...client, reminders_enabled: current }) // revert
    } else {
      toast(next ? 'Reminders on for this client' : 'Reminders off for this client', 'default')
    }
  }

  // ─── Client details edit ────────────────────────────────────────────────
  // Open editor: seed drafts with current values so the user is editing a
  // copy. Cancelling discards by closing without save.
  const handleStartEditDetails = () => {
    if (!client) return
    setDetailsDrafts({
      name:              client.name              || '',
      email:             client.email             || '',
      contactName:       client.contact_name      || '',
      internalReference: client.internal_reference || '',
      notes:             client.notes             || '',
    })
    setEditingDetails(true)
  }

  const handleCancelEditDetails = () => {
    if (savingDetails) return
    setEditingDetails(false)
  }

  const handleSaveDetails = async () => {
    // Validate: name and email are required to match the database NOT NULL
    // constraints. Other fields are free to be empty.
    if (!detailsDrafts.name?.trim()) {
      toast('Client name is required', 'error')
      return
    }
    if (!detailsDrafts.email?.trim()) {
      toast('Email is required', 'error')
      return
    }
    setSavingDetails(true)
    const { data, error } = await updateClientDetails(clientId, detailsDrafts)
    setSavingDetails(false)
    if (error) { toast(error.message, 'error'); return }
    if (data) setClient(data)
    toast('Client details saved ✓', 'success')
    setEditingDetails(false)
  }

  if (loading) return (
    <>
      <Navbar />
      <div className="loading-screen"><div className="spinner" /></div>
    </>
  )

  // Group requests into three bins. Cancelled requests are pulled out
  // first so they don't muddy the outstanding/completed split, which
  // is purely about transaction completion state.
  const cancelledRequests   = requests.filter(r =>  r.cancelled_at)
  const activeRequests      = requests.filter(r => !r.cancelled_at)
  const outstandingRequests = activeRequests.filter(r => !isCompleted(r))
  const completedRequests   = activeRequests.filter(r =>  isCompleted(r))
  const visibleRequests =
      activeTab === 'outstanding' ? outstandingRequests
    : activeTab === 'completed'   ? completedRequests
    : activeTab === 'cancelled'   ? cancelledRequests
    : outstandingRequests
  // Outstanding count is calculated from active (non-cancelled) requests
  // only. A cancelled request's pending transactions shouldn't appear as
  // "things still to chase".
  const totalOutstanding    = activeRequests.reduce((sum, req) => sum + getRequestCounts(req).pending, 0)
  const someSelected        = selected.size > 0
  const allVisibleSelected  = visibleRequests.length > 0 && visibleRequests.every(r => selected.has(r.id))

  const tabStyle = (tab) => ({
    padding: '9px 20px',
    borderRadius: 'var(--radius)',
    fontFamily: 'var(--font-body)',
    fontSize: '0.88rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    transition: 'all 0.15s',
    background: activeTab === tab ? 'var(--white)' : 'transparent',
    color: activeTab === tab ? 'var(--ink)' : 'var(--ink-muted)',
    boxShadow: activeTab === tab ? 'var(--shadow-sm)' : 'none',
  })

  const RequestList = ({ reqs }) => {
    if (reqs.length === 0) {
      const iconForTab = {
        outstanding: '✅',
        completed:   '📭',
        cancelled:   '⊘',
      }
      const titleForTab = {
        outstanding: 'Nothing outstanding',
        completed:   'No completed requests yet',
        cancelled:   'No cancelled requests',
      }
      const bodyForTab = {
        outstanding: 'All requests for this client are fully resolved.',
        completed:   'Requests appear here once all receipts are uploaded or marked as lost.',
        cancelled:   'Cancelled requests appear here. None yet for this client.',
      }
      return (
        <div className="card empty-state" style={{ padding: '48px 24px' }}>
          <div className="icon">{iconForTab[activeTab] || '✅'}</div>
          <h3>{titleForTab[activeTab] || 'Nothing here'}</h3>
          <p style={{ fontSize: '0.88rem' }}>
            {bodyForTab[activeTab] || ''}
          </p>
          {activeTab === 'outstanding' && (
            <Link to={`/clients/${clientId}/new-request`} className="btn btn-primary" style={{ marginTop: 8 }}>
              + New receipt request
            </Link>
          )}
        </div>
      )
    }

    return (
      <>
        {/* Select all */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '0 4px' }}>
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={() => toggleAll(reqs)}
            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }}
          />
          <span style={{ fontSize: '0.82rem', color: 'var(--ink-muted)' }}>
            {allVisibleSelected ? 'Deselect all' : 'Select all'}
          </span>
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          {reqs.map(req => {
            const { total, uploaded, lost, pending } = getRequestCounts(req)
            const resolved    = uploaded + lost
            const progressPct = total ? Math.round((resolved / total) * 100) : 0
            const isSelected  = selected.has(req.id)

            return (
              <div key={req.id} className="card" style={{
                padding: '16px 20px',
                border: `1.5px solid ${isSelected ? 'var(--accent)' : 'var(--surface-2)'}`,
                background: isSelected ? 'var(--accent-light)' : 'var(--white)',
                transition: 'all 0.15s',
              }}>
                <div className="flex-between" style={{ flexWrap: 'wrap', gap: 10, marginBottom: total > 0 ? 12 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(req.id)}
                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)', flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{req.title}</span>
                        {req.cancelled_at ? (
                          // Cancelled supersedes the other status pills.
                          // We show a "Cancelled" badge instead of misleading
                          // "X outstanding" or "Complete" labels, since the
                          // request is in neither of those states.
                          <span style={{ background: 'var(--surface-2)', color: 'var(--ink-muted)', fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px', borderRadius: 100 }}>
                            ⊘ Cancelled
                          </span>
                        ) : (
                          <>
                            {pending > 0 && (
                              <span style={{ background: 'var(--amber-light)', color: 'var(--amber)', fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px', borderRadius: 100 }}>
                                {pending} outstanding
                              </span>
                            )}
                            {pending === 0 && total > 0 && (
                              <span style={{ background: 'var(--green-light)', color: 'var(--green)', fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px', borderRadius: 100 }}>
                                ✓ Complete
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                        <span>📅 {formatDate(req.created_at)}</span>
                        <span>{total} transactions</span>
                        <span style={{ color: 'var(--green)' }}>✓ {uploaded} uploaded</span>
                        {lost > 0 && <span style={{ color: 'var(--amber)' }}>⚠ {lost} lost</span>}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                    <CopyLinkBtn token={req.token} />
                    <Link to={`/requests/${req.id}`} className="btn btn-secondary btn-sm">View →</Link>
                    {role !== 'assistant' && (
                      <button
                        className="btn btn-ghost btn-sm btn-icon"
                        onClick={() => handleDeleteSingle(req)}
                        title="Delete request"
                        style={{ color: '#dc2626' }}
                      >🗑</button>
                    )}
                  </div>
                </div>

                {total > 0 && (
                  <div style={{ height: 4, background: 'var(--surface-2)', borderRadius: 100, overflow: 'hidden', marginLeft: 28 }}>
                    <div style={{
                      height: '100%', width: `${progressPct}%`,
                      background: progressPct === 100 ? 'var(--green)' : 'var(--accent)',
                      borderRadius: 100, transition: 'width 0.4s ease',
                    }} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </>
    )
  }

  return (
    <>
      <Navbar />
      <PageHeader
        back={{ label: 'All clients', to: '/dashboard' }}
        title={client?.name}
        subtitle={
          // Always include the email so it's visible without opening edit
          // mode. Contact name and internal reference are appended when set,
          // in order: email · contact name · reference.
          client
            ? [
                client.email,
                client.contact_name,
                client.internal_reference,
              ].filter(Boolean).join(' · ')
            : ''
        }
        actions={
          // Hide the primary action when the client is archived. Archived
          // clients shouldn't receive new requests, only existing ones can
          // be reviewed.
          !client?.archived ? (
            <Link to={`/clients/${clientId}/new-request`} className="btn btn-primary">
              + New receipt request
            </Link>
          ) : null
        }
      />
      <main className="container" style={{ padding: '0 24px 40px' }}>

        {/* Archived banner. Mirrors the cancelled-request banner pattern on
            the request detail page so the visual language is consistent.
            Tells the bookkeeper exactly what the archived state means and
            gives a one-click escape if it was a mistake. */}
        {client?.archived && (
          <div style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--surface-3)',
            borderRadius: 'var(--radius)',
            padding: '14px 18px',
            marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--ink)' }}>📦 This client is archived.</strong>{' '}
              <span style={{ color: 'var(--ink-muted)' }}>
                They don't count toward your billing, no reminders will be
                sent, and clients can no longer upload to any of their
                requests. Existing data is preserved.
              </span>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleUnarchive}
              disabled={unarchiving}
            >
              {unarchiving ? 'Restoring…' : 'Unarchive client'}
            </button>
          </div>
        )}

        {/* ─── Meta row: outstanding count + reminders toggle + edit-details link
            Replaces three separate blocks (reminders toggle card, client details
            card, outstanding summary card) with one thin row. Notes content,
            when present, renders below this row, not in it.

            Order on the row, left to right:
              1. Outstanding count chip (mirrors the same info that appears in
                 the tab strip but acts as a quick at-a-glance summary that's
                 useful even when no tab is selected)
              2. Compact reminders toggle ("Reminders [on/off]")
              3. Notes summary or "Add notes" link, depending on whether the
                 client has notes set
              4. Edit details button, right-aligned
        */}
        {client && !editingDetails && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
            flexWrap: 'wrap',
            padding: '8px 0',
            marginBottom: 12,
            fontSize: '0.85rem',
          }}>
            {/* Outstanding count chip. Only shown when there are active
                (non-cancelled) requests and at least one is outstanding,
                otherwise the chip would be empty noise. */}
            {requests.length > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 100,
                background: totalOutstanding > 0 ? 'var(--amber-light)' : 'var(--green-light)',
                color:      totalOutstanding > 0 ? 'var(--amber)'       : 'var(--green)',
                fontWeight: 600, fontSize: '0.8rem',
              }}>
                {totalOutstanding > 0
                  ? `⏳ ${totalOutstanding} outstanding`
                  : '✓ All caught up'}
              </span>
            )}

            {/* Reminders toggle. Compact: label + small switch on one line.
                Same handler as before, just less chrome. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--ink-muted)' }}>Reminders</span>
              <button
                onClick={handleToggleClientReminders}
                aria-pressed={client.reminders_enabled !== false}
                title={client.reminders_enabled !== false
                  ? 'Auto-reminders on. Click to turn off for this client.'
                  : 'Auto-reminders off. Click to turn on for this client.'}
                style={{
                  width: 32, height: 18, borderRadius: 100,
                  background: (client.reminders_enabled !== false) ? 'var(--green)' : 'var(--surface-3)',
                  position: 'relative', cursor: 'pointer',
                  border: 'none', flexShrink: 0,
                  transition: 'background 0.2s',
                  padding: 0,
                }}
              >
                <span style={{
                  width: 12, height: 12, borderRadius: '50%', background: 'white',
                  position: 'absolute', top: 3,
                  left: (client.reminders_enabled !== false) ? 17 : 3,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  display: 'block',
                }} />
              </button>
            </span>

            {/* Notes hint / Add notes link. The actual notes content renders
                below this row when set. */}
            {!client.notes && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleStartEditDetails}
                style={{
                  color: 'var(--ink-muted)',
                  fontSize: '0.82rem',
                  padding: '0 6px',
                  height: 'auto',
                  fontWeight: 400,
                }}
              >
                📝 Add notes
              </button>
            )}

            {/* Right-aligned edit details link. Pushed to far right with
                margin-left auto. */}
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleStartEditDetails}
              style={{
                marginLeft: 'auto',
                color: 'var(--ink-muted)',
                fontSize: '0.82rem',
                padding: '0 6px',
                height: 'auto',
                fontWeight: 400,
              }}
            >
              Edit details
            </button>
          </div>
        )}

        {/* Inline notes display. Shows below the meta row when notes are set
            and we're not in edit mode. Short notes show in full; longer notes
            collapse to a truncated preview with a "more" toggle to expand
            inline. The visual is intentionally light so this doesn't read as
            another card competing with the request list below. */}
        {client && !editingDetails && client.notes && (
          <div style={{
            marginBottom: 14,
            padding: '10px 14px',
            background: 'var(--surface)',
            borderLeft: '3px solid var(--accent)',
            borderRadius: '0 6px 6px 0',
            fontSize: '0.86rem',
            color: 'var(--ink)',
            lineHeight: 1.55,
          }}>
            {(() => {
              const isLong = client.notes.length > NOTES_COLLAPSE_THRESHOLD
              const showFull = !isLong || notesExpanded
              return (
                <>
                  <div style={{
                    whiteSpace: showFull ? 'pre-wrap' : 'normal',
                    display: showFull ? 'block' : '-webkit-box',
                    WebkitLineClamp: showFull ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: showFull ? 'visible' : 'hidden',
                  }}>
                    {showFull
                      ? client.notes
                      // For the truncated preview, collapse newlines to spaces
                      // so two short lines of notes don't get cut off after
                      // one line.
                      : client.notes.replace(/\s+/g, ' ').slice(0, NOTES_COLLAPSE_THRESHOLD) + '…'}
                  </div>
                  {isLong && (
                    <button
                      onClick={() => setNotesExpanded(v => !v)}
                      style={{
                        background: 'none', border: 'none', padding: 0,
                        marginTop: 4,
                        color: 'var(--accent)', fontSize: '0.8rem',
                        cursor: 'pointer', fontWeight: 500,
                      }}
                    >
                      {notesExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </>
              )
            })()}
          </div>
        )}

        {/* Edit mode: full form. Only shown when editingDetails is true. */}
        {client && editingDetails && (
          <div className="card fade-up-1" style={{ padding: '16px 20px', marginBottom: 16 }}>
            <div style={{ marginBottom: 12, fontWeight: 500, fontSize: '0.88rem' }}>
              Edit client details
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px 16px',
              marginBottom: 14,
            }}>
              <ClientEditField
                label="Client / company name *"
                value={detailsDrafts.name}
                onChange={v => setDetailsDrafts(d => ({ ...d, name: v }))}
                placeholder="Acme Ltd"
              />
              <ClientEditField
                label="Email *"
                type="email"
                value={detailsDrafts.email}
                onChange={v => setDetailsDrafts(d => ({ ...d, email: v }))}
                placeholder="client@company.com"
              />
              <ClientEditField
                label="Contact name"
                value={detailsDrafts.contactName}
                onChange={v => setDetailsDrafts(d => ({ ...d, contactName: v }))}
                placeholder="Sarah Patel"
              />
              <ClientEditField
                label="Internal reference"
                value={detailsDrafts.internalReference}
                onChange={v => setDetailsDrafts(d => ({ ...d, internalReference: v }))}
                placeholder="e.g. ACME-001"
                maxLength={50}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: '0.74rem', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                color: 'var(--ink-muted)', marginBottom: 6,
              }}>
                Internal notes
              </div>
              <textarea
                className="input"
                rows={6}
                value={detailsDrafts.notes}
                onChange={e => setDetailsDrafts(d => ({ ...d, notes: e.target.value }))}
                placeholder="Anything other practice members should know about this client..."
                style={{ resize: 'vertical', minHeight: 90, fontFamily: 'inherit', lineHeight: 1.5 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={handleSaveDetails} disabled={savingDetails}>
                {savingDetails ? 'Saving…' : 'Save'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={handleCancelEditDetails} disabled={savingDetails}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Bulk action bar */}
        {someSelected && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            background: 'var(--ink)', color: 'white',
            padding: '12px 20px', borderRadius: 'var(--radius)',
            marginBottom: 16, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 500 }}>
              {selected.size} request{selected.size !== 1 ? 's' : ''} selected
            </span>
            {role !== 'assistant' && (
              <button
                className="btn btn-sm"
                style={{ background: '#dc2626', color: 'white', border: 'none' }}
                onClick={handleDeleteSelected}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? 'Deleting…' : '🗑 Delete selected'}
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: 'rgba(255,255,255,0.7)' }}
              onClick={() => setSelected(new Set())}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Tabs */}
        {requests.length > 0 && (
          <div className="fade-up-2" style={{
            display: 'inline-flex', gap: 4, padding: 4,
            background: 'var(--surface-2)', borderRadius: 'calc(var(--radius) + 4px)',
            marginBottom: 20,
          }}>
            <button style={tabStyle('outstanding')} onClick={() => { setActiveTab('outstanding'); setSelected(new Set()) }}>
              Outstanding
              {outstandingRequests.length > 0 && (
                <span style={{
                  marginLeft: 6, background: 'var(--amber-light)', color: 'var(--amber)',
                  fontSize: '0.72rem', fontWeight: 700, padding: '1px 6px', borderRadius: 100,
                }}>
                  {outstandingRequests.length}
                </span>
              )}
            </button>
            <button style={tabStyle('completed')} onClick={() => { setActiveTab('completed'); setSelected(new Set()) }}>
              Completed
              {completedRequests.length > 0 && (
                <span style={{
                  marginLeft: 6, background: 'var(--green-light)', color: 'var(--green)',
                  fontSize: '0.72rem', fontWeight: 700, padding: '1px 6px', borderRadius: 100,
                }}>
                  {completedRequests.length}
                </span>
              )}
            </button>
            {/* Cancelled tab. Only shown if at least one cancelled request
                exists, so it doesn't clutter clients who've never used the
                feature. Counts are styled neutrally (grey) since cancelled
                is neither good nor bad, just an end state. */}
            {cancelledRequests.length > 0 && (
              <button style={tabStyle('cancelled')} onClick={() => { setActiveTab('cancelled'); setSelected(new Set()) }}>
                Cancelled
                <span style={{
                  marginLeft: 6, background: 'var(--surface-2)', color: 'var(--ink-muted)',
                  fontSize: '0.72rem', fontWeight: 700, padding: '1px 6px', borderRadius: 100,
                }}>
                  {cancelledRequests.length}
                </span>
              </button>
            )}
          </div>
        )}

        {requests.length === 0 ? (
          <div className="card empty-state fade-up-2">
            <div className="icon">📤</div>
            <h3>No requests yet</h3>
            <p>Create a request by uploading a CSV of transactions that need receipts.</p>
            <Link to={`/clients/${clientId}/new-request`} className="btn btn-primary">
              + Create first request
            </Link>
          </div>
        ) : (
          <div className="fade-up-3">
            <RequestList reqs={visibleRequests} />
          </div>
        )}
      </main>
    </>
  )
}
